import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';

import Chart from './Chart.tsx';
import ChartCard from './ChartCard.tsx';
import { ChartCardMode } from './ChartCard.tsx';
import LegendItem from './LegendItem.tsx';
import TooltipContent from './TooltipContent.tsx';
import { FilterGroup } from '../../core/components/Filter.tsx';
import Stats from '../../core/components/Stats.tsx';
import useSearchParamState from '../../core/utils/useSearchParamState.tsx';
import { aggregateByMonth, formatDate, formatDateFull, getTicks } from '../utils/chartUtils.ts';

interface ChartProps {
    data: any[];
    [key: string]: any;
}

const MODE_PARAM_KEY = 'document-processing-chart-view';

const DocumentProcessingCard = ({
    data,
    ...props
}: Omit<React.ComponentPropsWithRef<typeof ChartCard>, 'title' | 'helpText' | 'controls'> & { data: any[] }) => {
    const { t } = useTranslation('analytics');

    const [mode, setMode] = useSearchParamState<ChartCardMode>(MODE_PARAM_KEY, ChartCardMode.OVER_TIME);

    const isDataValid = data != null && Array.isArray(data) && data.length > 0;

    return (
        <ChartCard
            title={t('dashboard.charts.documentProcessingCard.title')}
            helpText={t('dashboard.charts.documentProcessingCard.helpText')}
            controls={
                <FilterGroup>
                    <FilterGroup.Button
                        onClick={() => setMode(ChartCardMode.ACCUMULATED)}
                        active={mode === ChartCardMode.ACCUMULATED}
                    >
                        {t('dashboard.charts.documentProcessingCard.accumulated')}
                    </FilterGroup.Button>
                    <FilterGroup.Button
                        onClick={() => setMode(ChartCardMode.OVER_TIME)}
                        active={mode === ChartCardMode.OVER_TIME}
                    >
                        {t('dashboard.charts.documentProcessingCard.overTime')}
                    </FilterGroup.Button>
                </FilterGroup>
            }
            {...props}
        >
            {isDataValid &&
                (mode === ChartCardMode.ACCUMULATED ? (
                    <DocumentProcessingStats data={data} />
                ) : (
                    <DocumentProcessingChart data={data} />
                ))}
        </ChartCard>
    );
};

export default DocumentProcessingCard;

const DocumentProcessingStats = ({ data }: ChartProps) => {
    const { t } = useTranslation('analytics');

    const automatedCount = data?.reduce((acc, item) => acc + (item.automatedCount || 0), 0);
    const assistedCount = data?.reduce((acc, item) => acc + (item.assistedCount || 0), 0);
    const totalProcessedCount = automatedCount + assistedCount;

    const discardedCount = data?.reduce((acc, item) => acc + (item.discardedCount || 0), 0);
    const deletedCount = data?.reduce((acc, item) => acc + (item.deletedCount || 0), 0);
    const totalUnprocessedCount = discardedCount + deletedCount;

    const totalCount = automatedCount + assistedCount + discardedCount + deletedCount;

    return (
        <Stats>
            <Stats.Bar>
                <Stats.BarSection count={automatedCount} totalCount={totalCount} className="bg-data-data1" />
                <Stats.BarSection count={assistedCount} totalCount={totalCount} className="bg-data-data2" />
                <Stats.BarSection count={discardedCount} totalCount={totalCount} className="bg-data-data3" />
                <Stats.BarSection count={deletedCount} totalCount={totalCount} className="bg-data-data4" />
            </Stats.Bar>

            <Stats.Group>
                <Stats.Row>
                    <Stats.Label>
                        <LegendItem color="data1">{t('dashboard.charts.documentProcessingCard.automated')}</LegendItem>
                    </Stats.Label>

                    <Stats.Value count={automatedCount} totalCount={totalProcessedCount} />
                </Stats.Row>

                <Stats.Row>
                    <Stats.Label>
                        <LegendItem color="data2">{t('dashboard.charts.documentProcessingCard.assisted')}</LegendItem>
                    </Stats.Label>

                    <Stats.Value count={assistedCount} totalCount={totalProcessedCount} />
                </Stats.Row>

                <Stats.Divider />

                <Stats.Row>
                    <Stats.Label>{t('dashboard.charts.documentProcessingCard.totalDocuments')}</Stats.Label>
                    <Stats.Value count={totalProcessedCount} totalCount={totalProcessedCount} />
                </Stats.Row>
            </Stats.Group>

            <Stats.Group>
                <Stats.Row>
                    <Stats.Label>
                        <LegendItem color="data3">{t('dashboard.charts.documentProcessingCard.discarded')}</LegendItem>
                    </Stats.Label>

                    <Stats.Value count={discardedCount} totalCount={totalUnprocessedCount} />
                </Stats.Row>

                <Stats.Row>
                    <Stats.Label>
                        <LegendItem color="data4">{t('dashboard.charts.documentProcessingCard.deleted')}</LegendItem>
                    </Stats.Label>

                    <Stats.Value count={deletedCount} totalCount={totalUnprocessedCount} />
                </Stats.Row>

                <Stats.Divider />

                <Stats.Row>
                    <Stats.Label>{t('dashboard.charts.documentProcessingCard.totalDocuments')}</Stats.Label>
                    <Stats.Value count={totalUnprocessedCount} totalCount={totalUnprocessedCount} />
                </Stats.Row>
            </Stats.Group>

            <Stats.Group className="bg-surface-transparent py-1">
                <Stats.Row>
                    <Stats.Label>{t('dashboard.charts.documentProcessingCard.totalDocuments')}</Stats.Label>
                    <Stats.Value count={totalCount} totalCount={totalCount} />
                </Stats.Row>
            </Stats.Group>
        </Stats>
    );
};

const DocumentProcessingChart = ({ data }: ChartProps) => {
    const { t, i18n } = useTranslation('analytics');

    const dayCount = data.length;
    const isMonthly = dayCount > 32;
    const processedData = isMonthly
        ? aggregateByMonth(data, ['automatedCount', 'assistedCount', 'discardedCount', 'deletedCount'])
        : data;
    const height = 400;
    const barSize = Math.max(20, Math.min(80, processedData.length <= 7 ? 60 : processedData.length <= 15 ? 40 : 30));

    const maxValue = Math.max(
        ...processedData.map((d) =>
            Math.max(d.automatedCount || 0, d.assistedCount || 0, d.discardedCount || 0, d.deletedCount || 0)
        )
    );
    // round to the next value that is dividable by 10 and by 4
    const factor = 20;
    const maxDisplayValue = Math.ceil(maxValue / factor) * factor;

    return (
        <Chart>
            <Chart.Legend>
                <LegendItem className="text-content-medium" color="data1">
                    {t('dashboard.charts.documentProcessingCard.automated')}
                </LegendItem>

                <LegendItem className="text-content-medium" color="data2">
                    {t('dashboard.charts.documentProcessingCard.assisted')}
                </LegendItem>

                <LegendItem className="text-content-medium" color="data3">
                    {t('dashboard.charts.documentProcessingCard.discarded')}
                </LegendItem>

                <LegendItem className="text-content-medium" color="data4">
                    {t('dashboard.charts.documentProcessingCard.deleted')}
                </LegendItem>
            </Chart.Legend>

            <Chart.Content>
                <Chart.FixedSide>
                    <BarChart
                        data={processedData}
                        margin={{ top: 0, left: 0, right: 0, bottom: 10 }}
                        width={70}
                        height={height}
                    >
                        <XAxis
                            dataKey="date"
                            width={0}
                            axisLine={false}
                            tickLine={false}
                            fill="transparent"
                            stroke="transparent"
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            className="text-xs fill-content-strong"
                            domain={[0, maxDisplayValue]}
                            allowDecimals={false}
                            ticks={getTicks(5, maxDisplayValue)}
                            tickFormatter={(value) => value.toLocaleString(i18n.language)}
                        />
                        <Bar dataKey="automated" fill="transparent" stroke="transparent" />
                    </BarChart>
                </Chart.FixedSide>

                <Chart.Scrollable>
                    <ResponsiveContainer
                        width={processedData.length < 10 ? '100%' : processedData.length * 200}
                        height={height}
                    >
                        <BarChart
                            data={processedData}
                            margin={{ right: 30, bottom: 10 }}
                            width={processedData.length * 200}
                            height={height}
                        >
                            <CartesianGrid vertical={false} stroke="inherit" className="stroke-line-weak" />
                            <RechartsTooltip
                                cursor={<Chart.TooltipCursor />}
                                content={({ payload }) => {
                                    const date = payload?.[0]?.payload.date;
                                    const formattedDate = date && formatDateFull(i18n.language, date, isMonthly);

                                    return (
                                        <TooltipContent>
                                            <TooltipContent.Title>{formattedDate}</TooltipContent.Title>
                                            <TooltipContent.Row>
                                                <LegendItem className="text-content-white" color="data1">
                                                    {t('dashboard.charts.documentProcessingCard.automated')}
                                                </LegendItem>
                                                <span className="font-medium">
                                                    {payload?.[0]?.payload.automatedCount || 0}
                                                </span>
                                            </TooltipContent.Row>
                                            <TooltipContent.Row>
                                                <LegendItem className="text-content-white" color="data2">
                                                    {t('dashboard.charts.documentProcessingCard.assisted')}
                                                </LegendItem>
                                                <span className="font-medium">
                                                    {payload?.[0]?.payload.assistedCount || 0}
                                                </span>
                                            </TooltipContent.Row>
                                            <TooltipContent.Row>
                                                <LegendItem className="text-content-white" color="data3">
                                                    {t('dashboard.charts.documentProcessingCard.discarded')}
                                                </LegendItem>
                                                <span className="font-medium">
                                                    {payload?.[0]?.payload.discardedCount || 0}
                                                </span>
                                            </TooltipContent.Row>
                                            <TooltipContent.Row>
                                                <LegendItem className="text-content-white" color="data4">
                                                    {t('dashboard.charts.documentProcessingCard.deleted')}
                                                </LegendItem>
                                                <span className="font-medium">
                                                    {payload?.[0]?.payload.deletedCount || 0}
                                                </span>
                                            </TooltipContent.Row>
                                        </TooltipContent>
                                    );
                                }}
                            />
                            <XAxis
                                type="category"
                                dataKey="date"
                                tick={{ fill: 'inherit' }}
                                className="text-xs fill-content-medium"
                                tickFormatter={(date) => formatDate(i18n.language, date, isMonthly)}
                                tickMargin={20}
                                minTickGap={20}
                                tickLine={false}
                                axisLine={false}
                                padding={{ left: 20, right: 20 }}
                            />
                            <YAxis domain={[0, maxDisplayValue]} ticks={getTicks(5, maxDisplayValue)} hide />
                            <Bar dataKey="automatedCount" className="fill-data-data1" barSize={barSize} />
                            <Bar dataKey="assistedCount" className="fill-data-data2" barSize={barSize} />
                            <Bar dataKey="discardedCount" className="fill-data-data3" barSize={barSize} />
                            <Bar dataKey="deletedCount" className="fill-data-data4" barSize={barSize} />
                        </BarChart>
                    </ResponsiveContainer>
                </Chart.Scrollable>
            </Chart.Content>
        </Chart>
    );
};
